import { useState } from 'react'
import {
  Alert,
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Snackbar,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { Navigate } from 'react-router-dom'
import useAuthStore from '../store/authStore'
import { getApiErrorMessage } from '../services/apiClient'
import { usePriceConcerns } from '../hooks/usePriceConcerns'
import { PriceConcernsSummaryBar } from '../components/price-concerns/PriceConcernsSummaryBar'
import { PriceConcernsFilters } from '../components/price-concerns/PriceConcernsFilters'
import { PriceConcernsTable } from '../components/price-concerns/PriceConcernsTable'
import { PriceConcernDetailDrawer } from '../components/price-concerns/PriceConcernDetailDrawer'
import { isImageProof, isPdfProof } from '../components/price-concerns/priceConcernUtils'

/** Scroll area for the concerns table: vertical cap + horizontal scroll on narrow viewports. */
const priceConcernsTableScrollSx = {
  width: '100%',
  minWidth: 0,
  maxWidth: '100%',
  '& > .MuiPaper-root': {
    maxHeight: {
      xs: 'min(420px, calc(100dvh - 300px))',
      sm: 'min(480px, calc(100dvh - 280px))',
      md: 'min(560px, calc(100dvh - 260px))',
      lg: 'min(640px, calc(100dvh - 240px))',
    },
    overflowX: 'auto',
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
  },
  '& .MuiTableHead-root .MuiTableCell-head': {
    position: 'sticky',
    top: 0,
    zIndex: 2,
    bgcolor: '#f8f9fb',
    boxShadow: 'inset 0 -1px 0 rgba(224, 224, 224, 1)',
  },
  '& .MuiTablePagination-root': {
    position: 'sticky',
    bottom: 0,
    zIndex: 2,
    bgcolor: 'background.paper',
    borderTop: '1px solid',
    borderColor: 'divider',
    flexWrap: 'wrap',
    gap: 0.5,
    px: { xs: 0.5, sm: 1 },
    '& .MuiToolbar-root': {
      flexWrap: 'wrap',
      justifyContent: { xs: 'center', sm: 'flex-end' },
      minHeight: { xs: 48, sm: 52 },
      pl: { xs: 0.5, sm: 2 },
      pr: { xs: 0.5, sm: 1 },
    },
  },
}

const PriceConcernsPage = () => {
  const isSuperuserActor = useAuthStore((s) => s.isSuperuserActor)

  const [filters, setFilters] = useState({
    search: '',
    status: '',
    datePreset: '',
    dateFrom: '',
    dateTo: '',
    page: 0,
    rowsPerPage: 20,
  })

  const [detail, setDetail] = useState(null)
  const [proofLightbox, setProofLightbox] = useState(null)
  const [snackbar, setSnackbar] = useState(null)
  const [statusLogRefreshKey, setStatusLogRefreshKey] = useState(0)

  const {
    rows,
    total,
    summary,
    listLoading,
    summaryLoading,
    exporting,
    error,
    setError,
    statusUpdatingId,
    updateStatus,
    exportExcel,
  } = usePriceConcerns(filters)

  if (!isSuperuserActor) {
    return <Navigate to="/dashboard" replace />
  }

  const handleStatusChange = async (id, nextStatus) => {
    try {
      const result = await updateStatus(id, nextStatus)
      setSnackbar({
        severity: 'success',
        message:
          nextStatus === 'REVIEWED'
            ? 'Marked as reviewed.'
            : 'Marked as not reviewed.',
      })
      setDetail((prev) =>
        prev && prev.id === id ? { ...prev, status: nextStatus } : prev,
      )
      setStatusLogRefreshKey((k) => k + 1)
      // If the filtered page is now empty, step back so pagination stays valid.
      if (typeof result?.total === 'number') {
        const maxPage = Math.max(
          0,
          Math.ceil(result.total / filters.rowsPerPage) - 1,
        )
        if (filters.page > maxPage) {
          setFilters((prev) => ({ ...prev, page: maxPage }))
        }
      }
    } catch (err) {
      setSnackbar({
        severity: 'error',
        message: getApiErrorMessage(err, 'Failed to update status'),
      })
    }
  }

  const handleExport = async () => {
    try {
      await exportExcel()
      setSnackbar({ severity: 'success', message: 'Excel downloaded.' })
    } catch (err) {
      setSnackbar({
        severity: 'error',
        message: getApiErrorMessage(err, 'Failed to export'),
      })
    }
  }

  const openProof = (row) => {
    const proof = row?.proof
    if (!proof?.url) {
      setSnackbar({ severity: 'info', message: 'No proof URL available.' })
      return
    }
    if (isImageProof(proof)) {
      setProofLightbox(proof)
      return
    }
    window.open(proof.url, '_blank', 'noopener,noreferrer')
  }

  return (
    <Box
      sx={{
        p: { xs: 1.5, sm: 2, md: 3 },
        maxWidth: 1360,
        mx: 'auto',
        width: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        overflowX: 'hidden',
      }}
    >
      <PriceConcernsSummaryBar
        summary={summary}
        loading={summaryLoading}
        exporting={exporting}
        onExport={handleExport}
      />

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2, wordBreak: 'break-word' }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      <Box sx={{ minWidth: 0, maxWidth: '100%' }}>
        <PriceConcernsFilters filters={filters} onFiltersChange={setFilters} />
      </Box>

      <Box sx={priceConcernsTableScrollSx}>
        <PriceConcernsTable
          rows={rows}
          total={total}
          loading={listLoading}
          page={filters.page}
          rowsPerPage={filters.rowsPerPage}
          statusUpdatingId={statusUpdatingId}
          onPageChange={(page) => setFilters((prev) => ({ ...prev, page }))}
          onRowsPerPageChange={(rowsPerPage) =>
            setFilters((prev) => ({ ...prev, rowsPerPage, page: 0 }))
          }
          onOpenDetail={setDetail}
          onStatusChange={handleStatusChange}
          onOpenProof={openProof}
        />
      </Box>

      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        textAlign="center"
        sx={{ mt: 2, px: { xs: 0.5, sm: 0 }, wordBreak: 'break-word' }}
      >
        This page is for review only — export to Excel to forward the full report to the
        business team.
      </Typography>

      <PriceConcernDetailDrawer
        open={!!detail}
        concern={detail}
        statusLogRefreshKey={statusLogRefreshKey}
        onClose={() => setDetail(null)}
      />

      <Dialog
        open={!!proofLightbox}
        onClose={() => setProofLightbox(null)}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            mx: { xs: 1, sm: 2 },
            width: { xs: 'calc(100% - 16px)', sm: 'auto' },
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
          <IconButton onClick={() => setProofLightbox(null)} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ pt: 0 }}>
          {proofLightbox?.url ? (
            <Box
              component="img"
              src={proofLightbox.url}
              alt={proofLightbox.name || 'Proof'}
              sx={{ width: '100%', maxHeight: '80vh', objectFit: 'contain' }}
            />
          ) : null}
          {proofLightbox && isPdfProof(proofLightbox) ? (
            <Typography>Open the PDF in a new tab from the detail drawer.</Typography>
          ) : null}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={5000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? (
          <Alert
            severity={snackbar.severity || 'info'}
            onClose={() => setSnackbar(null)}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        ) : null}
      </Snackbar>
    </Box>
  )
}

export default PriceConcernsPage
